import type { Action } from '@empathybroker/tsclient-play/dist/models/action'
import type { CurationTool } from '@empathyco/playboard-core-domain'
import type Vue from 'vue'
import type { Location } from 'vue-router'
import type { FeatureNode } from '../../models'
import { getCurrentInstance } from 'vue'
import { CRUD_CONSTS, TOOLING_CONSTS } from '../../store'
import { useFeatureRoutes } from '../use-feature-routes'
import { mapActions } from '../utils'

export interface OptimizeMenuComposable {
  getLinkRoute: (feature: FeatureNode, query: string) => Promise<Location>
}

export const useOptimizeMenu = (): OptimizeMenuComposable => {
  const {
    proxy: { $store },
  } = getCurrentInstance() as { proxy: Vue }

  const { getCreateLocation, getUpdateLocation, getListLocation, isFeatureConfigurableByQuery } =
    useFeatureRoutes()

  const { toolingList } = mapActions($store, TOOLING_CONSTS.moduleName, {
    toolingList: TOOLING_CONSTS.actions.list,
  })

  const { list } = mapActions($store, CRUD_CONSTS.moduleName, {
    list: CRUD_CONSTS.actions.list,
  })

  async function getLinkRoute(feature: FeatureNode, query: string): Promise<Location> {
    const isConfigurableByQuery = isFeatureConfigurableByQuery(feature)
    const isRoutesBaseConfigured = feature.routeNameBase

    if (isConfigurableByQuery && isRoutesBaseConfigured) {
      const { featureConfigs, enabledConfigs } = await fetchFeatureConfigsByQuery({
        feature,
        query,
      })

      if (featureConfigs.length === 0) {
        return getCreateLocation(feature)
      }

      if (enabledConfigs.length === 1) {
        return getUpdateLocation({ id: enabledConfigs[0].id, feature })
      }
    }

    return getListLocation({ feature, query })
  }

  async function fetchFeatureConfigsByQuery({
    feature,
    query,
  }: {
    feature: FeatureNode
    query: string
  }): Promise<Record<string, any>> {
    let listResponse
    let featureConfigs
    let enabledConfigs
    try {
      listResponse = await toolingList({
        tool: feature.id,
        params: {
          filters: { query },
          strictMatching: true,
        },
      })
      featureConfigs = listResponse.results
      enabledConfigs = (featureConfigs as CurationTool[]).filter(item => item.criteria.enabled)
    } catch {
      listResponse = await list({
        dataType: feature.id,
        query,
        strictMatching: true,
      })
      featureConfigs = listResponse.data.items
      enabledConfigs = (featureConfigs as Action[]).filter(item => item.enabled)
    }

    return { featureConfigs, enabledConfigs }
  }

  return {
    getLinkRoute,
  }
}
